import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { SendIcon, SparklesIcon, TrashIcon, Loader2Icon } from 'lucide-react';
import OrionConstellation from '../components/OrionConstellation';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'orion';
  timestamp: Date;
}
const OrionChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [constellationComplete, setConstellationComplete] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const suggestedQuestions = ['¿Qué servicios ofrece Grupo Reflex?', '¿Cómo proyectar la grandeza de mi marca?', '¿Qué tipo de contenido conecta con mi audiencia?', '¿Por qué invertir en producción audiovisual?'];
  const handleSend = async (text?: string) => {
    const messageText = text || inputValue;
    if (!messageText.trim() || isLoading) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/orion/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: messageText
        })
      });
      if (!response.ok) {
        throw new Error('Error al procesar la solicitud');
      }
      const data = await response.json();
      const orionMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        sender: 'orion',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, orionMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Lo siento, ocurrió un error al procesar tu mensaje. Por favor, intenta de nuevo.',
        sender: 'orion',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleClear = () => {
    setMessages([]);
  };
  return <section id="ask-orion" className="min-h-screen bg-gray-900 relative overflow-hidden py-20" data-id="element-411">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-10" data-id="element-412">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" data-id="element-413"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl" data-id="element-414"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-415">
        {/* Constellation Animation */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} transition={{
        duration: 1
      }} viewport={{
        once: true
      }} className="mb-12" data-id="element-416">
          <OrionConstellation starCount={14} color="#a855f7" onComplete={() => setConstellationComplete(true)} data-id="element-417" />
        </motion.div>
        {/* Chat Interface */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: constellationComplete ? 1 : 0,
        y: constellationComplete ? 0 : 50
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="max-w-4xl mx-auto" data-id="element-418">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-purple-500/20 overflow-hidden" data-id="element-419">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6" data-id="element-420">
              <div className="flex items-center justify-between" data-id="element-421">
                <div className="flex items-center" data-id="element-422">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-423">
                    <SparklesIcon className="h-6 w-6 text-purple-600" data-id="element-424" />
                  </div>
                  <div data-id="element-425">
                    <h3 className="text-2xl font-bold text-white" data-id="element-426">ORION</h3>
                    <p className="text-white/80 italic" data-id="element-427">
                      Tu guía inteligente en creatividad, estrategia y
                      comunicación.
                    </p>
                  </div>
                </div>
                {messages.length > 0 && <button onClick={handleClear} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors" title="Limpiar conversación" data-id="element-428">
                    <TrashIcon className="h-5 w-5" data-id="element-429" />
                  </button>}
              </div>
            </div>
            {/* Messages */}
            <div className="h-96 overflow-y-auto p-6 space-y-4" data-id="element-430">
              {messages.length === 0 && <div className="text-center text-gray-400 py-8" data-id="element-431">
                  <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-purple-400" data-id="element-432" />
                  <p className="text-lg mb-6" data-id="element-433">¿En qué puedo ayudarte hoy?</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto" data-id="element-434">
                    {suggestedQuestions.map((question, index) => <button key={index} onClick={() => handleSend(question)} className="text-left p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors text-sm text-gray-300 hover:text-white border border-purple-500/20 hover:border-purple-500/40" data-id="element-435">
                        {question}
                      </button>)}
                  </div>
                </div>}
              {messages.map(message => <motion.div key={message.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-436">
                  <div className={`max-w-[80%] rounded-2xl p-4 ${message.sender === 'user' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-700 text-gray-100'}`} data-id="element-437">
                    <p className="text-sm whitespace-pre-line" data-id="element-438">
                      {message.text}
                    </p>
                    <p className="text-xs mt-2 opacity-70" data-id="element-439">
                      {message.timestamp.toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </p>
                  </div>
                </motion.div>)}
              {isLoading && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-start" data-id="element-440">
                  <div className="bg-gray-700 rounded-2xl p-4 flex items-center gap-2" data-id="element-441">
                    <Loader2Icon className="h-5 w-5 text-purple-400 animate-spin" data-id="element-442" />
                    <span className="text-gray-300" data-id="element-443">
                      ORION está pensando...
                    </span>
                  </div>
                </motion.div>}
              <div ref={messagesEndRef} data-id="element-444" />
            </div>
            {/* Input */}
            <div className="p-6 bg-gray-800/50 border-t border-purple-500/20" data-id="element-445">
              <div className="flex gap-3" data-id="element-446">
                <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Pregúntame sobre Grupo Reflex o sobre marketing…" disabled={isLoading} className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-400 disabled:opacity-50" data-id="element-447" />
                <button onClick={() => handleSend()} disabled={!inputValue.trim() || isLoading} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2" data-id="element-448">
                  {isLoading ? <Loader2Icon className="h-5 w-5 animate-spin" data-id="element-449" /> : <SendIcon className="h-5 w-5" data-id="element-450" />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
export default OrionChat;